import type { HttpErrorResponse } from '@angular/common/http';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import type {
  AiScreeningConfig,
  AiScreeningCreateConfig,
  AiScreeningCriterionVerification,
  AiScreeningUpdateConfig,
  GeneratedCriterion,
  VerifyCriteriaPayload,
} from '@recruitee/ai-screening-types';

export const jobTemplateIdAlias = (jobTemplateId: number) => `jobTemplateId:${jobTemplateId}`;

const idSelector = (config: { id: number }) => config.id;

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<AiScreeningConfig>()(
  'JobTemplatesAiScreeningConfig',
  {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        fetchOne: (
          state,
          payload: { offerId: number; correlationId: string; hasHireAssistant: boolean },
        ) => adapter.markAsPending(state, { entities: [payload.correlationId], scope: null }),
        fetchOneSuccess: (state, payload: { data: AiScreeningConfig; correlationId: string }) =>
          pipeState(
            state,
            next => defaultActions.fetchOneSuccess(next, payload),
            next => adapter.setAlias(next, { [payload.correlationId]: payload.data.id }),
            next =>
              adapter.setAlias(next, { [jobTemplateIdAlias(payload.data.id)]: payload.data.id }),
          ),

        updateOnePessimistic: (
          state,
          payload: { offerId: number; data: AiScreeningUpdateConfig; hasHireAssistant: boolean },
        ) => adapter.markAsPending(state, { entities: [payload.data.id], scope: null }),

        createOnePessimistic: (
          state,
          payload: {
            offerId: number;
            data: AiScreeningCreateConfig;
            correlationId: string;
            hasHireAssistant: boolean;
          },
        ) => adapter.markAsPending(state, { entities: [payload.correlationId], scope: null }),

        generateCriteria: ngrxEvent<{ offerId: number; hasHireAssistant: boolean }>(),
        generateCriteriaSuccess: ngrxEvent<{ data: GeneratedCriterion[] }>(),
        generateCriteriaFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

        verifyCriteria: ngrxEvent<{
          offerId: number;
          data: VerifyCriteriaPayload;
          hasHireAssistant: boolean;
        }>(),
        verifyCriteriaSuccess: ngrxEvent<{ data: AiScreeningCriterionVerification[] }>(),
        verifyCriteriaFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);

const AiScreeningConfigSelectors = {
  byId: selectors.byId,
  error: selectors.error,
  isPending: selectors.isPending,
  isLoaded: selectors.isLoaded,
};

export {
  actions as AiScreeningConfigActions,
  AiScreeningConfigSelectors,
  reducer as AiScreeningConfigReducer,
  namespace as AiScreeningConfigNamespace,
};
